// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// Single neighbor row for SimilarItemsDialog — shows structured title/lineage
// when available, plus match strength and short "why" reasons.

import { SimilarNeighbor } from "@/hooks/prometheus-agent/useSimilar";

function matchLabel(similarity: number): string {
  if (similarity >= 0.85) return "Strong match";
  if (similarity >= 0.7) return "Good match";
  return "Related";
}

function matchPercent(similarity: number): string {
  return `${Math.round(similarity * 100)}%`;
}

/** Prefer structured fields; fall back to first content line as a title. */
function displayTitle(n: SimilarNeighbor): string {
  const titled = (n.title || "").trim();
  if (titled) return titled;
  const first = (n.content || "").trim().split("\n")[0]?.trim();
  return first || "(untitled)";
}

interface SimilarNeighborRowProps {
  neighbor: SimilarNeighbor;
  selectable?: boolean;
  onSelect?: (sourceId: string) => void;
  hint?: string;
}

export function SimilarNeighborRow({
  neighbor: n,
  selectable,
  onSelect,
  hint,
}: SimilarNeighborRowProps) {
  const title = displayTitle(n);
  const lineage = (n.lineage || "").trim();
  const breeder = (n.breeder || "").trim();
  const subtitle = (n.subtitle || "").trim();
  const reasons = (n.reasons || []).filter(Boolean);
  const hasStructured = Boolean(n.title || n.lineage || n.breeder || n.subtitle);

  return (
    <li
      className={`rounded border border-border p-3 text-sm ${selectable ? "cursor-pointer hover:bg-muted/60 transition-colors" : ""}`}
      onClick={() => onSelect?.(n.source_id)}
      role={selectable ? "button" : undefined}
      tabIndex={selectable ? 0 : undefined}
      onKeyDown={(e) => {
        if (!selectable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.(n.source_id);
        }
      }}
    >
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-xs text-muted-foreground">{matchLabel(n.similarity)}</span>
        <span className="text-xs font-medium tabular-nums text-foreground/80">
          {matchPercent(n.similarity)}
        </span>
      </div>

      <p className="font-medium text-foreground leading-snug">{title}</p>

      {lineage && (
        <p className="mt-0.5 text-sm text-muted-foreground leading-snug">
          <span className="text-muted-foreground/70">Lineage </span>
          {lineage}
        </p>
      )}

      {(breeder || subtitle) && (
        <p className="mt-0.5 text-xs text-muted-foreground">
          {breeder || subtitle}
        </p>
      )}

      {reasons.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {reasons.map((reason) => (
            <span
              key={reason}
              className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground"
            >
              {reason}
            </span>
          ))}
        </div>
      )}

      {!hasStructured && n.content && (
        <p className="mt-1 line-clamp-2 text-muted-foreground/90">{n.content}</p>
      )}

      {selectable && hint && (
        <p className="mt-2 text-[11px] text-muted-foreground/80">{hint} →</p>
      )}
    </li>
  );
}
